/**
 * Browser Compatibility and Feature Detection Utility
 * Ensures cross-browser compatibility and provides graceful fallbacks
 */

class BrowserCompatibility {
  constructor() {
    this.browserInfo = this.detectBrowser();
    this.featureSupport = this.detectFeatures();
    this.performanceMetrics = {};
  }

  // Detect browser type and version
  detectBrowser() {
    const ua = navigator.userAgent;
    const browserInfo = {
      name: 'Unknown',
      version: 'Unknown',
      engine: 'Unknown',
      mobile: false,
      supported: true
    };

    // Detect mobile
    browserInfo.mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    // Detect browser
    if (ua.includes('Chrome') && !ua.includes('Edg')) {
      browserInfo.name = 'Chrome';
      browserInfo.engine = 'Blink';
      const match = ua.match(/Chrome\/(\d+)/);
      browserInfo.version = match ? match[1] : 'Unknown';
    } else if (ua.includes('Firefox')) {
      browserInfo.name = 'Firefox';
      browserInfo.engine = 'Gecko';
      const match = ua.match(/Firefox\/(\d+)/);
      browserInfo.version = match ? match[1] : 'Unknown';
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      browserInfo.name = 'Safari';
      browserInfo.engine = 'WebKit';
      const match = ua.match(/Version\/(\d+)/);
      browserInfo.version = match ? match[1] : 'Unknown';
    } else if (ua.includes('Edg')) {
      browserInfo.name = 'Edge';
      browserInfo.engine = 'Blink';
      const match = ua.match(/Edg\/(\d+)/);
      browserInfo.version = match ? match[1] : 'Unknown';
    } else if (ua.includes('Opera') || ua.includes('OPR')) {
      browserInfo.name = 'Opera';
      browserInfo.engine = 'Blink';
      const match = ua.match(/(?:Opera|OPR)\/(\d+)/);
      browserInfo.version = match ? match[1] : 'Unknown';
    } else if (ua.includes('MSIE') || ua.includes('Trident')) {
      browserInfo.name = 'Internet Explorer';
      browserInfo.engine = 'Trident';
      browserInfo.supported = false;
      const match = ua.match(/(?:MSIE |rv:)(\d+)/);
      browserInfo.version = match ? match[1] : 'Unknown';
    }

    // Mark unsupported browsers
    if (browserInfo.name === 'Internet Explorer' ||
        (browserInfo.name === 'Safari' && parseInt(browserInfo.version) < 12) ||
        (browserInfo.name === 'Chrome' && parseInt(browserInfo.version) < 70)) {
      browserInfo.supported = false;
    }

    console.log('[BrowserCompatibility] Detected browser:', browserInfo);
    return browserInfo;
  }

  // Detect feature support
  detectFeatures() {
    const features = {
      // Core Web APIs
      fetch: typeof fetch !== 'undefined',
      promises: typeof Promise !== 'undefined',
      asyncAwait: this.checkAsyncAwaitSupport(),

      // Storage APIs
      localStorage: this.checkLocalStorageSupport(),
      sessionStorage: this.checkSessionStorageSupport(),
      indexedDB: typeof indexedDB !== 'undefined',

      // Service Worker
      serviceWorker: 'serviceWorker' in navigator,

      // Performance APIs
      performance: 'performance' in window,
      performanceMemory: 'memory' in performance,

      // Modern APIs
      intersectionObserver: 'IntersectionObserver' in window,
      mutationObserver: 'MutationObserver' in window,
      resizeObserver: 'ResizeObserver' in window,
      webGL: this.checkWebGLSupport(),

      // ES6+ Features
      arrowFunctions: this.checkArrowFunctionSupport(),
      templateLiterals: this.checkTemplateLiteralSupport(),
      destructuring: this.checkDestructuringSupport(),
      modules: this.checkModuleSupport(),

      // Security features
      contentSecurityPolicy: this.checkCSPLevel(),
      httpsOnly: window.location.protocol === 'https:',
      mixedContent: this.checkMixedContentSupport()
    };

    console.log('[BrowserCompatibility] Feature support detected:', features);
    return features;
  }

  // Check async/await support
  checkAsyncAwaitSupport() {
    // Check for native async function support
    return typeof Promise !== 'undefined' &&
           typeof Promise.prototype.then !== 'undefined' &&
           typeof async function() {}.constructor === 'function';
  }

  // Check localStorage support
  checkLocalStorageSupport() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Check sessionStorage support
  checkSessionStorageSupport() {
    try {
      const test = '__storage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Check WebGL support
  checkWebGLSupport() {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        canvas.getContext('webgl'));
    } catch (e) {
      return false;
    }
  }

  // Check arrow function support
  checkArrowFunctionSupport() {
    // Check for arrow function support by testing function length
    try {
      const arrowFn = () => {};
      return typeof arrowFn === 'function' && arrowFn.length === 0;
    } catch (e) {
      return false;
    }
  }

  // Check template literal support
  checkTemplateLiteralSupport() {
    // Check for template literal support
    try {
      const test = `template`;
      return typeof test === 'string' && test === 'template';
    } catch (e) {
      return false;
    }
  }

  // Check destructuring support
  checkDestructuringSupport() {
    // Check for destructuring support
    try {
      const obj = { a: 1, b: 2 };
      const { a } = obj;
      return a === 1;
    } catch (e) {
      return false;
    }
  }

  // Check ES module support
  checkModuleSupport() {
    return 'noModule' in document.createElement('script');
  }

  // Check CSP level support
  checkCSPLevel() {
    // Check for CSP support through various methods
    const metaTags = document.getElementsByTagName('meta');
    for (let i = 0; i < metaTags.length; i++) {
      if (metaTags[i].getAttribute('http-equiv') === 'Content-Security-Policy') {
        return 'CSP 1.0+';
      }
    }
    return 'None detected';
  }

  // Check mixed content support
  checkMixedContentSupport() {
    return window.location.protocol === 'https:';
  }

  // Apply browser-specific optimizations
  applyBrowserOptimizations() {
    const { name, version } = this.browserInfo;

    // Chrome-specific optimizations
    if (name === 'Chrome') {
      this.applyChromeOptimizations(version);
    }

    // Firefox-specific optimizations
    if (name === 'Firefox') {
      this.applyFirefoxOptimizations(version);
    }

    // Safari-specific optimizations
    if (name === 'Safari') {
      this.applySafariOptimizations(version);
    }

    // Edge-specific optimizations
    if (name === 'Edge') {
      this.applyEdgeOptimizations(version);
    }

    console.log(`[BrowserCompatibility] Applied ${name} specific optimizations`);
  }

  // Chrome-specific optimizations
  applyChromeOptimizations(version) {
    // Chrome has good performance, but we can optimize for specific versions
    if (parseInt(version) >= 90) {
      // Enable advanced performance features
      if ('scheduler' in window) {
        console.log('[BrowserCompatibility] Chrome: Advanced scheduler available');
      }
    }

    // Chrome-specific CSS optimizations
    document.documentElement.style.setProperty('--chrome-optimized', 'true');
  }

  // Firefox-specific optimizations
  applyFirefoxOptimizations(version) {
    // Firefox has different performance characteristics
    if (parseInt(version) >= 88) {
      // Enable Firefox-specific features
      console.log('[BrowserCompatibility] Firefox: Modern version detected');
    }

    // Firefox-specific CSS optimizations
    document.documentElement.style.setProperty('--firefox-optimized', 'true');
  }

  // Safari-specific optimizations
  applySafariOptimizations(version) {
    // Safari has unique performance characteristics
    if (parseInt(version) >= 14) {
      console.log('[BrowserCompatibility] Safari: Modern version detected');
    }

    // Safari-specific CSS optimizations
    document.documentElement.style.setProperty('--safari-optimized', 'true');

    // Fix Safari-specific issues
    this.fixSafariIssues();
  }

  // Edge-specific optimizations
  applyEdgeOptimizations(version) {
    if (parseInt(version) >= 90) {
      console.log('[BrowserCompatibility] Edge: Chromium-based version detected');
    }

    // Edge-specific CSS optimizations
    document.documentElement.style.setProperty('--edge-optimized', 'true');
  }

  // Fix Safari-specific issues
  fixSafariIssues() {
    // Fix Safari flexbox issues
    const style = document.createElement('style');
    style.textContent = `
      /* Safari flexbox fixes */
      .safari-flex-fix {
        display: -webkit-box;
        display: -webkit-flex;
        display: -ms-flexbox;
        display: flex;
      }

      /* Safari sticky positioning fix */
      .safari-sticky-fix {
        position: -webkit-sticky;
        position: sticky;
      }
    `;
    document.head.appendChild(style);
  }

  // Provide fallbacks for unsupported features
  provideFeatureFallbacks() {
    const fallbacks = [];

    // Fetch API fallback
    if (!this.featureSupport.fetch) {
      this.provideFetchFallback();
      fallbacks.push('Fetch API');
    }

    // Promise fallback
    if (!this.featureSupport.promises) {
      this.providePromiseFallback();
      fallbacks.push('Promises');
    }

    // IntersectionObserver fallback
    if (!this.featureSupport.intersectionObserver) {
      this.provideIntersectionObserverFallback();
      fallbacks.push('IntersectionObserver');
    }

    // ResizeObserver fallback
    if (!this.featureSupport.resizeObserver) {
      this.provideResizeObserverFallback();
      fallbacks.push('ResizeObserver');
    }

    // WebGL fallback
    if (!this.featureSupport.webGL) {
      this.provideWebGLFallback();
      fallbacks.push('WebGL');
    }

    if (fallbacks.length > 0) {
      console.log('[BrowserCompatibility] Applied fallbacks for:', fallbacks.join(', '));
    }

    return fallbacks;
  }

  // Provide fetch API fallback
  provideFetchFallback() {
    // Simple XMLHttpRequest-based fetch polyfill
    if (!window.fetch) {
      window.fetch = function(url, options = {}) {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.open(options.method || 'GET', url);

          if (options.headers) {
            Object.keys(options.headers).forEach(key => {
              xhr.setRequestHeader(key, options.headers[key]);
            });
          }

          xhr.onload = () => {
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              statusText: xhr.statusText,
              text: () => Promise.resolve(xhr.responseText),
              json: () => Promise.resolve(JSON.parse(xhr.responseText))
            });
          };

          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(options.body);
        });
      };
    }
  }

  // Provide Promise fallback
  providePromiseFallback() {
    // Simple Promise polyfill
    if (!window.Promise) {
      // Using a basic Promise implementation
      console.warn('[BrowserCompatibility] Promise polyfill required but not implemented');
    }
  }

  // Provide IntersectionObserver fallback
  provideIntersectionObserverFallback() {
    if (!window.IntersectionObserver) {
      // Simple fallback implementation
      window.IntersectionObserver = class IntersectionObserver {
        constructor(callback) {
          this.callback = callback;
          this.elements = new Set();
        }

        observe(element) {
          this.elements.add(element);
          // Trigger immediately for basic functionality
          setTimeout(() => {
            this.callback([{
              target: element,
              isIntersecting: true,
              intersectionRatio: 1
            }]);
          }, 100);
        }

        unobserve(element) {
          this.elements.delete(element);
        }

        disconnect() {
          this.elements.clear();
        }
      };
    }
  }

  // Provide ResizeObserver fallback
  provideResizeObserverFallback() {
    if (!window.ResizeObserver) {
      window.ResizeObserver = class ResizeObserver {
        constructor(callback) {
          this.callback = callback;
          this.elements = new Set();
        }

        observe(element) {
          this.elements.add(element);
          // Basic implementation - trigger on window resize
          const handleResize = () => {
            this.callback([{
              target: element,
              contentRect: element.getBoundingClientRect()
            }]);
          };

          window.addEventListener('resize', handleResize);
          element._resizeHandler = handleResize;
        }

        unobserve(element) {
          if (element._resizeHandler) {
            window.removeEventListener('resize', element._resizeHandler);
          }
          this.elements.delete(element);
        }

        disconnect() {
          this.elements.forEach(element => this.unobserve(element));
        }
      };
    }
  }

  // Provide WebGL fallback
  provideWebGLFallback() {
    // WebGL fallback - disable WebGL-dependent features
    console.warn('[BrowserCompatibility] WebGL not supported, some features may be disabled');
    window._webGLDisabled = true;
  }

  // Check if browser is supported
  isBrowserSupported() {
    return this.browserInfo.supported;
  }

  // Get browser support warning message
  getBrowserSupportMessage() {
    if (this.isBrowserSupported()) {
      return null;
    }

    const { name, version } = this.browserInfo;
    return `Your browser (${name} ${version}) may not be fully supported. For the best experience, please use a modern browser like Chrome, Firefox, Safari, or Edge.`;
  }

  // Initialize browser compatibility system
  initialize() {
    console.log('[BrowserCompatibility] Initializing browser compatibility system...');

    // Apply optimizations
    this.applyBrowserOptimizations();

    // Provide fallbacks
    const fallbacks = this.provideFeatureFallbacks();

    // Set browser-specific CSS classes
    this.setBrowserCSSClasses();

    // Log compatibility status
    const status = {
      browser: this.browserInfo,
      features: this.featureSupport,
      fallbacks,
      supported: this.isBrowserSupported()
    };

    console.log('[BrowserCompatibility] Compatibility status:', status);

    // Store compatibility info globally for debugging
    window.browserCompatibility = status;

    return status;
  }

  // Set browser-specific CSS classes
  setBrowserCSSClasses() {
    const { name, version, mobile } = this.browserInfo;

    const classes = [
      `browser-${name.toLowerCase()}`,
      `browser-version-${version}`,
      mobile ? 'device-mobile' : 'device-desktop',
      this.isBrowserSupported() ? 'browser-supported' : 'browser-unsupported'
    ];

    document.documentElement.classList.add(...classes);
  }

  // Get performance metrics
  getPerformanceMetrics() {
    const metrics = {
      browser: this.browserInfo,
      features: this.featureSupport,
      timing: {},
      memory: {}
    };

    // Get navigation timing
    if (this.featureSupport.performance) {
      const timing = performance.getEntriesByType('navigation')[0];
      if (timing) {
        metrics.timing = {
          loadTime: timing.loadEventEnd - timing.fetchStart,
          domReady: timing.domContentLoadedEventEnd - timing.fetchStart,
          firstPaint: this.getFirstPaintTime()
        };
      }
    }

    // Get memory info
    if (this.featureSupport.performanceMemory) {
      metrics.memory = {
        used: performance.memory.usedJSHeapSize,
        total: performance.memory.totalJSHeapSize,
        limit: performance.memory.jsHeapSizeLimit
      };
    }

    this.performanceMetrics = metrics;
    return metrics;
  }

  // Get first paint time
  getFirstPaintTime() {
    const paintEntries = performance.getEntriesByType('paint');
    const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
    return firstPaint ? firstPaint.startTime : null;
  }

  // Export compatibility report
  exportCompatibilityReport() {
    return {
      timestamp: new Date().toISOString(),
      browser: this.browserInfo,
      features: this.featureSupport,
      performance: this.getPerformanceMetrics(),
      recommendations: this.generateCompatibilityRecommendations()
    };
  }

  // Generate compatibility recommendations
  generateCompatibilityRecommendations() {
    const recommendations = [];

    if (!this.isBrowserSupported()) {
      recommendations.push({
        type: 'critical',
        message: 'Browser upgrade recommended',
        action: 'Upgrade to a modern browser for full functionality'
      });
    }

    // Feature-specific recommendations
    if (!this.featureSupport.serviceWorker) {
      recommendations.push({
        type: 'info',
        message: 'Service Worker not supported',
        action: 'Some offline features may not be available'
      });
    }

    if (!this.featureSupport.webGL) {
      recommendations.push({
        type: 'warning',
        message: 'WebGL not supported',
        action: 'Some visual features may be disabled'
      });
    }

    if (!this.featureSupport.localStorage) {
      recommendations.push({
        type: 'critical',
        message: 'Local storage not available',
        action: 'Application may not function properly'
      });
    }

    return recommendations;
  }
}

// Create singleton instance
const browserCompatibility = new BrowserCompatibility();

// Auto-initialize when imported
if (typeof window !== 'undefined') {
  // Small delay to ensure DOM is ready
  setTimeout(() => {
    browserCompatibility.initialize();
  }, 100);
}

export default browserCompatibility;
